import {useEffect, useRef} from "react";
import confetti from "canvas-confetti";

const ANCHOR_STYLE = {
  position: "fixed",
  width: 0,
  height: 0,
  overflow: "hidden",
  pointerEvents: "none",
};

const BURST_OPTIONS = {particleCount: 100, spread: 70, origin: {y: 0.5}};
const DRIP_OPTIONS = {
  particleCount: 6,
  spread: 180,
  startVelocity: 3,
  gravity: 0.8,
  ticks: 150,
};

const DRIP_CANVAS_STYLE =
  "position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:999";
const DRIP_TICK_MS = 400;
const DRIP_DURATION_MS = 10000;

const isSpent = (key) => {
  try {
    return Boolean(key && sessionStorage.getItem(key));
  } catch {
    return false;
  }
};

const spend = (key) => {
  try {
    if (key) sessionStorage.setItem(key, "1");
  } catch {
    // Storage unavailable: the effect replays on the next visit.
  }
};

const getDripOrigin = (anchor, offset) => {
  const rect = anchor?.getBoundingClientRect();
  return {
    x:
      (rect ? (rect.left + rect.width / 2) / window.innerWidth : 0.5) +
      offset.x / window.innerWidth,
    y: (rect ? rect.bottom / window.innerHeight : 0) + offset.y / window.innerHeight,
  };
};

const Confetti = ({
  trigger,
  drip,
  moveDripX = 0,
  moveDripY = 0,
  limitOncePerSession,
  sessionKey = "confetti_fired",
}) => {
  const anchorRef = useRef(null);
  const offsetRef = useRef({x: moveDripX, y: moveDripY});
  const key = limitOncePerSession ? sessionKey : null;

  useEffect(() => {
    offsetRef.current = {x: moveDripX, y: moveDripY};
  }, [moveDripX, moveDripY]);

  useEffect(() => {
    if (!trigger || isSpent(key)) return;
    confetti(BURST_OPTIONS);
    spend(key);
  }, [trigger, key]);

  useEffect(() => {
    if (!drip || isSpent(key)) return;

    const canvas = document.createElement("canvas");
    canvas.style.cssText = DRIP_CANVAS_STYLE;
    document.body.appendChild(canvas);

    const dripConfetti = confetti.create(canvas, {resize: true});
    let marked = false;

    const interval = setInterval(() => {
      if (!marked) {
        spend(key);
        marked = true;
      }
      dripConfetti({
        ...DRIP_OPTIONS,
        origin: getDripOrigin(anchorRef.current, offsetRef.current),
      });
    }, DRIP_TICK_MS);

    const timeout = setTimeout(() => clearInterval(interval), DRIP_DURATION_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
      dripConfetti.reset();
      canvas.remove();
    };
  }, [drip, key]);

  return <span ref={anchorRef} style={ANCHOR_STYLE} />;
};

export default Confetti;
